import React, { useState } from 'react'
import styled from 'styled-components'
import useProductLocationById from '../hooks/useProductLocationById'
import CustomBlockLoader from '../../../knisot/generic/CustomBlockLoader'
import LocationIcon from '../../../../../../generic/icons/IconLib/MapPinIcon'
import ScalesIcon from '../../../../../../generic/icons/IconLib/ScaleIcon'
import PackageIcon from '../../../../../../generic/icons/IconLib/PackageBoxIcon'
import CalendatDay from '../../../../../../generic/icons/IconLib/CalendarIcon'
import { ILocation } from '../../../../../../modules/API/Warehouse/models/responses'
import { TLocationType } from '../../../../../../modules/API/Warehouse/models/locations'
import ProductImage from '../../../queries/TeudotDetails/Components/ProductImage'
import { convertToDate } from '../../../../../../modules/convertDateString'
import { isValidYmdDate } from '../../../../../../generic/utils/forDate/isValidYmdDate'
import KbdChipButton from '../../../../../../generic/ui/KbdChipButton/KbdChipButton'
import BarcodeIcon from '../../../../../../generic/icons/IconLib/BarcodeIcon'
import { RadioSlider, RadioSliderItem } from '../../../../../../generic/ui/Toggles/RadioSlider'
import {
  LOCATION_TYPE_FILTER_OPTIONS,
  TLocationTypeFilter,
  defaultLocationTypeFilter,
  filterByLocationType,
} from '../../../../../../generic/artcoded/locationType'

// הכל | ליקוט | אחסון — picking sits in the middle and is the default view (falls back to הכל when empty).
const TYPE_TABS: RadioSliderItem[] = LOCATION_TYPE_FILTER_OPTIONS.map((option) => ({
  id: String(option.value),
  label: option.label,
}))
const PREFERRED_TYPE: TLocationType = 1

type Props = {
  productID: number
  varietyID: number
  sizeID: string
  packID: number
  /** Выбор книсы из локации: получает location.EntryDetailNum и запускает флоу как при скане. */
  onSelectEntry?: (entryCode: string) => void
}

const LocationForProduct = ({
  productID,
  varietyID,
  sizeID,
  packID,
  onSelectEntry,
}: Props) => {
  const { data, isLoading, error } = useProductLocationById({
    productID,
    varietyID,
    sizeID,
    packID,
  })
  // null — the user hasn't picked a tab yet, so the default follows the data.
  const [pickedFilter, setPickedFilter] = useState<TLocationTypeFilter | null>(null)
  
  if(isLoading) return <CustomBlockLoader />
  
  if(error || !data || !data.Locations || data.Locations.length === 0) {
    return <EmptyMessage>אין מיקומים זמינים</EmptyMessage>
  }

  const typeFilter = pickedFilter ?? defaultLocationTypeFilter(data.Locations, PREFERRED_TYPE)
  const locations = filterByLocationType(data.Locations, typeFilter)

  return (
    <LocationsContainer>
      <ProductCard>
        <ImageSection>
          <ProductImage 
            id={data.GeneralProductID}
            size="lg"
          />
        </ImageSection>
        
      </ProductCard>
      <TypeSwitchSlot>
        <RadioSlider
          items={TYPE_TABS}
          value={String(typeFilter)}
          onChange={(id) => setPickedFilter(Number(id) as TLocationTypeFilter)}
          ariaLabel='סוג איתור'
        />
      </TypeSwitchSlot>
      {locations.length === 0 && <EmptyMessage>אין מיקומים מסוג זה</EmptyMessage>}
      {locations.map((location: ILocation, index: number) => (
        <LocationCard key={location.Code || index}>
          <LocationInfo>
            <LocationRow>
              <LocationIconWrapper>
                <LocationIcon />
              </LocationIconWrapper>
              <LocationDescription>{location.Description || location.Code}</LocationDescription>
              {isValidYmdDate(location.ExpirationDate) && (
                <>
                  <LocationIconWrapper>
                    <CalendatDay />
                  </LocationIconWrapper>
                  <LocationDescription>{convertToDate(location.ExpirationDate)}</LocationDescription>
                </>
              )}
            </LocationRow>
            <LocationDetails>
              <DetailItem>
                <DetailIcon>
                  <ScalesIcon />
                </DetailIcon>
                <DetailValue>{location.BalanceWeight?.toFixed(2) || 0}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailIcon>
                  <PackageIcon />
                </DetailIcon>
                <DetailValue>{location.BalanceAmount || 0}</DetailValue>
              </DetailItem>
            </LocationDetails>
          </LocationInfo>
          {onSelectEntry && (
            <SelectEntrySlot>
              <KbdChipButton
                type='button'
                onClick={() => onSelectEntry(`${location.EntryDetailNum}`)}
                aria-label='בחירת כניסה'
                title='בחירת כניסה'
              >
                <BarcodeIcon size={16} color='currentColor' />
              </KbdChipButton>
            </SelectEntrySlot>
          )}
        </LocationCard>
      ))}
    </LocationsContainer>
  )
}

const LocationsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75em;
  overflow-y: auto;
  padding: 0.5em;
  max-height: 500px;
  width: 100%;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

const TypeSwitchSlot = styled.div`
  display: flex;
  justify-content: center;
`
const ProductCard = styled.div`
  display: flex;
  width: 100%;
  gap: 1.5em;
  padding: 1em;
  justify-content: center;
`

const ImageSection = styled.div`
  flex-shrink: 0;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

const InfoSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75em;
  min-width: 0;
`
const LocationCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75em;
  padding: 1em 1.25em;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
`

const SelectEntrySlot = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin-inline-end: 0.25em;
`

const LocationInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  flex: 1;
  min-width: 0;
`

const LocationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1em;
`

const LocationDetails = styled.div`
  display: flex;
  gap: 1em;
  align-items: center;
  justify-content: flex-start;
`

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
`

const DetailIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #297F76;
  font-size: 0.9em;
`

const DetailValue = styled.span`
  color: #2c3e50;
  font-weight: 500;
  font-size: 0.9em;
`

const LocationIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #297F76;
  font-size: 1em;
  flex-shrink: 0;
`

const LocationDescription = styled.span`
  color: #2c3e50;
  font-weight: 500;
  font-size: 0.95em;
`

const EmptyMessage = styled.div`
  padding: 2em;
  text-align: center;
  color: #7f8c8d;
  font-size: 1em;
`

export default LocationForProduct